'use client';

import { useRef, type ChangeEvent, type FC, useMemo } from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';

import { ClickableDiv } from '@wwe-portal/ui/components/button/clickable-div';
import { Input } from '@wwe-portal/ui/components/input/input';
import type { SelectItemProps } from '@wwe-portal/ui/features/club-store/components';
import { RosterAttributeSelect } from '@wwe-portal/ui/features/club-store/components';
import { useElementSize } from '@wwe-portal/ui/hooks/use-element-size';
import { groupBy } from '@wwe-portal/ui/lib/group-by';
import titleBgWebp from '@wwe-portal/ui/assets/images/roster-header.webp';

import { SelectableRosterStar } from './roster-star';
import type { PlayerRosterForClubStore } from '@wwe-portal/entities/roster.type';

export interface RosterFilterData {
  era: string | null;
  stable: string | null;
  class: string | null;
}

interface FilterItem {
  title: string;
  items: SelectItemProps[];
  clearFilterText: string;
  onClearFilter: () => void;
  onSelectItem: (id: string) => void;
}

export interface RosterFilterProps {
  rosters: PlayerRosterForClubStore[];
  filterData: Record<keyof RosterFilterData, FilterItem>;
  filterValue: RosterFilterData;
  searchText: string;
  title: string;

  onChangeSearch?: (e: ChangeEvent<HTMLInputElement>) => void;
  onClickRoster: (rosterId: string) => void;
  noItemText: React.ReactNode;
}

const PLAYER_CARDS_COLUMNS = 3;

// @warning: This component is deprecated. Use RosterFilterV2 instead.
export const RosterFilter: FC<RosterFilterProps> = ({
  rosters: items,
  searchText,
  filterData,
  filterValue,
  onChangeSearch,
  onClickRoster,
  noItemText,
  title,
}) => {
  const [elementRef, { width }] = useElementSize();

  const groupCards = useMemo(
    () =>
      groupBy(
        items,
        (_, index) => Math.floor(index / PLAYER_CARDS_COLUMNS),
        () => PLAYER_CARDS_COLUMNS
      ).map((item) => item.children),
    [items]
  );

  // https://github.com/Qovery/console/pull/904
  // Wait til radix-ui resolve scroll issue in dialog. The only work around is putting dropdown in dialog content
  const ref = useRef<HTMLDivElement>(null);
  const parentRef = useRef(null);

  // The virtualizer
  const rowVirtualizer = useVirtualizer({
    count: Math.ceil(items.length / PLAYER_CARDS_COLUMNS),
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => (index === items.length - 1 ? 115 : 115 + 12),
  });

  const rosterCard = (item: PlayerRosterForClubStore) => {
    return (
      <ClickableDiv
        className="relative w-[88px] overflow-hidden"
        onClick={() => onClickRoster(item.id)}
      >
        <img
          alt={item.id}
          className="bg-back200 h-[115px] rounded-md object-contain object-center"
          src={item.img}
        />
        <div className="absolute inset-x-0 bottom-2">
          {item.level != null && item.star != null && (
            <SelectableRosterStar
              className="w-full justify-center gap-[1px] drop-shadow-[0.3px_0.3px_1px_rgba(0,0,0,0.7)]"
              hollowStar={item.hollowStar}
              level={item.level}
              maxRate={item.star + (item.hollowStar || 0)}
              rate={item.star}
              size={8}
            />
          )}
        </div>
      </ClickableDiv>
    );
  };

  return (
    <div
      className="font-poppins relative flex h-[698px] max-h-[calc(100dvh-88px)] w-full flex-col gap-2 overflow-hidden rounded-[10px] px-2 pb-[14px] pt-7"
      ref={ref}
    >
      <div className="h-10 basis-0">
        <div
          className="text-text300 flex h-10 w-full items-center justify-center bg-contain text-sm font-medium"
          style={{ backgroundImage: `url(${titleBgWebp})` }}
        >
          <span className="text-yellow800 font-semibold">{title}</span>
        </div>
      </div>

      <Input
        onChange={onChangeSearch}
        placeholder="Search for Roster"
        tabIndex={-1}
        value={searchText}
      />

      <div
        className="text-text300 grid w-full grid-cols-3 text-center text-sm font-medium"
        ref={elementRef}
      >
        <RosterAttributeSelect
          align="start"
          container={ref.current}
          innerWidth={width}
          selectedId={filterValue.class}
          {...filterData.class}
        />
        <RosterAttributeSelect
          align="center"
          container={ref.current}
          innerWidth={width}
          selectedId={filterValue.era}
          {...filterData.era}
        />
        <RosterAttributeSelect
          align="end"
          container={ref.current}
          innerWidth={width}
          selectedId={filterValue.stable}
          {...filterData.stable}
        />
      </div>

      <div className="max-h-[495px] grow overflow-auto">
        {items.length ? (
          <div
            className="no-scrollbar"
            ref={parentRef}
            style={{
              height: '100%',
              overflow: 'auto', // Make it scroll!
            }}
          >
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                return (
                  <div
                    key={virtualItem.key}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      width: '100%',
                      height: `115px`,
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                  >
                    <div className="grid grid-cols-3 place-items-center gap-2 px-2.5 text-center">
                      {groupCards[virtualItem.index]?.map(
                        (card: PlayerRosterForClubStore) => (
                          <div key={card.id}>{rosterCard(card)}</div>
                        )
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            {noItemText}
          </div>
        )}
      </div>
    </div>
  );
};
